import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CalendarClock, Save } from "lucide-react";

const WEEKDAYS = [
  { value: 0, label: "Monday" },
  { value: 1, label: "Tuesday" },
  { value: 2, label: "Wednesday" },
  { value: 3, label: "Thursday" },
  { value: 4, label: "Friday" },
  { value: 5, label: "Saturday" },
  { value: 6, label: "Sunday" },
];

function formatHour(h) {
  return `${String(h).padStart(2, "0")}:00`;
}

export default function BriefingScheduleSection() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get("/settings/briefing-schedule")
      .then(({ data }) => setState(data))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || e.message));
  }, []);

  if (!state) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500" data-testid="briefing-schedule-loading">
        Loading briefing schedule…
      </div>
    );
  }

  const save = async () => {
    setBusy(true);
    setErr("");
    try {
      await api.put("/settings/briefing-schedule", {
        briefing_frequency: state.briefing_frequency,
        briefing_weekday: state.briefing_weekday,
        briefing_hour: state.briefing_hour,
      });
      toast.success("Briefing schedule updated.");
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const isWeekly = state.briefing_frequency === "weekly";
  const preview = isWeekly
    ? `${WEEKDAYS.find((w) => w.value === state.briefing_weekday)?.label || "Monday"} at ${formatHour(state.briefing_hour)}`
    : `Every day at ${formatHour(state.briefing_hour)}`;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="briefing-schedule-card">
      <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 flex items-center gap-1.5">
        <CalendarClock className="h-3.5 w-3.5" /> Briefing schedule
      </div>
      <p className="mt-2 text-sm text-slate-500">
        When your AI briefing lands in your inbox. Times use your workspace timezone (
        <span className="font-mono text-slate-700">{state.timezone}</span>).
      </p>

      <div className="mt-6 space-y-5">
        {/* Frequency */}
        <div>
          <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Frequency</label>
          <div className="mt-2 inline-flex items-center rounded-full bg-slate-100 p-1" data-testid="briefing-frequency-toggle">
            <button
              onClick={() => setState((s) => ({ ...s, briefing_frequency: "weekly" }))}
              data-testid="briefing-frequency-weekly"
              className={`px-4 py-1.5 text-xs font-semibold rounded-full transition ${
                isWeekly ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Weekly
            </button>
            <button
              onClick={() => setState((s) => ({ ...s, briefing_frequency: "daily" }))}
              data-testid="briefing-frequency-daily"
              className={`px-4 py-1.5 text-xs font-semibold rounded-full transition ${
                !isWeekly ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Daily
            </button>
          </div>
        </div>

        {/* Weekday (only weekly) */}
        {isWeekly && (
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Day of week</label>
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="briefing-weekday-picker">
              {WEEKDAYS.map((d) => (
                <button
                  key={d.value}
                  onClick={() => setState((s) => ({ ...s, briefing_weekday: d.value }))}
                  data-testid={`briefing-weekday-${d.value}`}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition ${
                    state.briefing_weekday === d.value
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  {d.label.slice(0, 3)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Hour */}
        <div>
          <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Send hour</label>
          <div className="mt-2">
            <select
              value={state.briefing_hour}
              onChange={(e) => setState((s) => ({ ...s, briefing_hour: Number(e.target.value) }))}
              data-testid="briefing-hour-select"
              className="rounded-lg border border-slate-300 bg-white text-sm px-3 py-2 min-w-[110px]"
            >
              {Array.from({ length: 24 }, (_, i) => i).map((h) => (
                <option key={h} value={h}>
                  {formatHour(h)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-sm text-slate-700" data-testid="briefing-preview">
          Preview: <span className="font-semibold text-slate-900">{preview}</span>
        </div>

        {err && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">{err}</div>
        )}

        <div>
          <button
            onClick={save}
            disabled={busy}
            data-testid="briefing-schedule-save"
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800 disabled:opacity-60"
          >
            <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save schedule"}
          </button>
        </div>
      </div>
    </div>
  );
}
